/**
 * Form Builder Types
 * Define types for form configuration and form builder components
 */
import { ReactNode, ReactElement } from 'react'
import type { RegisterOptions } from 'react-hook-form'

// Define size types compatible with MUI Grid components
export type GridSizeType = boolean | 'auto' | number | undefined

// Form field types
export enum FieldType {
  TEXT = 'text',
  SELECT = 'select',
  MULTISELECT = 'multiselect',
  SWITCH = 'switch',
  CHECKBOX = 'checkbox',
  READONLY = 'readonly',
  DATE = 'date',
  DATE_TIME = 'datetime',
  DATE_RANGE = 'daterange',
  AUTOCOMPLETE = 'autocomplete',
  RADIO_CARD = 'radiocard'
}

// Option type for select fields
export interface FieldOption {
  value: string | number
  label: string
}

// Data source types
export enum DataSourceType {
  URL = 'url', // URL-based API request
  FUNCTION = 'function' // Based on predefined functions
}

// Function type data source configuration
export interface FunctionDataSourceConfig {
  key: string // Data source unique identifier
  type: DataSourceType.FUNCTION // Data source type
  service: (...args: any[]) => Promise<any> // Data loading function
  params?: Record<string, any> // Parameters passed to the function
  dependencies?: string[] // Dependent fields, reload data when these fields change
  transform?: (data: any) => any // Data transformation function
  errorHandler?: (error: any) => void // Error handling function
  autoLoad?: boolean // Whether to auto-load, default is true
}

// URL type data source configuration
export interface UrlDataSourceConfig {
  key: string // Data source unique identifier
  type: DataSourceType.URL // Data source type
  url: string // API request address
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' // HTTP method, default is GET
  params?: Record<string, any> // URL parameters
  body?: Record<string, any> // Request body
  headers?: Record<string, any> // Request headers
  dependencies?: string[] // Dependent fields, reload data when these fields change
  transform?: (data: any) => any // Data transformation function
  errorHandler?: (error: any) => void // Error handling function
  autoLoad?: boolean // Whether to auto-load, default is true
}

// Data source configuration (union type)
export type DataSourceConfig = UrlDataSourceConfig | FunctionDataSourceConfig

// Base form field configuration
export interface FormFieldConfig {
  name: string
  label: string
  type: FieldType | ((values: Record<string, any>) => FieldType) // Support dynamic type based on form values
  defaultValue?: any
  placeholder?: string
  required?: boolean
  disabled?: boolean
  readOnly?: boolean // Field-level readonly property
  options?: FieldOption[]
  helperText?: string | ReactNode
  enableChangeHighlight?: boolean // Control whether to enable change highlighting for this field
  size?:
    | GridSizeType
    | {
        xs?: GridSizeType
        sm?: GridSizeType
        md?: GridSizeType
        lg?: GridSizeType
        xl?: GridSizeType
      } // Control field size in Grid

  // Data source configuration
  dataSource?: string // Referenced data source key
  valueField?: string // Field name used as value in data source
  labelField?: string // Field name used as label in data source

  // Dynamic form functionality
  dependencies?: string[] // Fields this field depends on
  visible?: (values: Record<string, any>) => boolean // Dynamic visibility
  transform?: (value: any, allValues: Record<string, any>) => any // Transform value at collection time (getAllValues / onChange callback)
  formatOnBlur?: (value: any) => any // Format value when field loses focus (e.g. pad decimals "1" → "1.00")

  // Additional props passed to the field component
  fieldProps?: Record<string, any>

  // Auto select first option when data source is loaded
  autoSelectFirst?: boolean

  // Validation rules (uses react-hook-form RegisterOptions)
  // This will be merged with the basic 'required' rule
  rules?: Omit<RegisterOptions, 'required'>
}

// Field linkage rule action types
export enum LinkageActionType {
  CLEAR = 'clear', // Clear target field value
  SET_VALUE = 'setValue', // Set target field value
  RELOAD_DATA = 'reloadData' // Reload target field's data source
}

// Field linkage rule
export interface LinkageRule {
  triggerField: string // Trigger field name
  targetField: string // Target field name
  action: LinkageActionType // Action type
  condition?: (triggerValue: any, allValues: Record<string, any>) => boolean // Optional condition function
  value?: any | ((triggerValue: any, allValues: Record<string, any>) => any) // Value to set (for SET_VALUE action)
  dataSourceKey?: string // Data source key (for RELOAD_DATA action)
}

// Form configuration
export interface FormConfig {
  fields: FormFieldConfig[]
  dataSources?: DataSourceConfig[] // Form data source configuration
  linkageRules?: LinkageRule[] // Field linkage rules
}

// Form change tracking
export interface FormChange {
  field: string
  oldValue: any
  newValue: any
}

// Data source state
export interface DataSourceState {
  loading: boolean
  data: any[] | null
  error: any | null
  options?: FieldOption[] // Transformed options
}

// Data source collection state
export interface DataSourcesState {
  [key: string]: DataSourceState
}

// Render configuration form items function type
export type RenderConfigFieldsFunction = () =>
  | ReactElement
  | ReactElement[]
  | null

// Form builder props
export interface FormBuilderProps {
  config: FormConfig
  gridSpacing?: number
  initialValues?: Record<string, any>
  /** react-hook-form validation mode. Default is 'onSubmit'. Use 'onChange' to re-validate as user types. */
  validationMode?: 'onSubmit' | 'onBlur' | 'onChange' | 'onTouched' | 'all'
  onChange?: (values: Record<string, any>, changes: FormChange[]) => void
  onDataSourcesReady?: () => void // Add callback for when all data sources are loaded and ready
  onBlur?: (
    field: { name: string; value: any },
    values: Record<string, any>
  ) => void
  readOnly?: boolean | string[] // True for all fields or array of field names
  children?:
    | ReactNode
    | ((renderConfigFields: RenderConfigFieldsFunction) => ReactNode)
  renderFields?: boolean // Control whether to render fields in the configuration
}

// Form field props
export interface FormFieldProps {
  config: FormFieldConfig
  control: any // react-hook-form control
  errors: any // react-hook-form errors
  values: Record<string, any>
  readOnly?: boolean | string[] // True for all fields or array of field names
}

// Form builder ref methods
export interface FormBuilderRef {
  // Function to collect all values
  getAllValues: () => Record<string, any>

  // Function to collect specific form item values
  getFieldValues: (fieldNames: string | string[]) => any

  // Function to reset to initial data
  resetForm: () => void

  // Function to collect all changed form items
  getChangedFields: () => FormChange[]

  // Form validation function
  validateForm: () => Promise<boolean>

  // Silent form validation function (returns result without persisting validation UI errors)
  // Optionally accepts a list of field names to validate; if omitted, validates all fields
  validateFormSilently: (fieldNames?: string[]) => Promise<boolean>

  // Form submission function
  submitForm: () => Promise<{
    values: Record<string, any>
    changes: FormChange[]
  }>

  // Set form item value
  setFieldValue: (
    name: string,
    value: any,
    options?: { shouldValidate?: boolean }
  ) => void

  // Function to render configuration form items
  renderConfigFields: RenderConfigFieldsFunction

  // Get data source data
  getDataSource: (key: string) => DataSourceState | null

  // Manual reload data source
  reloadDataSource: (key: string) => Promise<void>

  // Manually apply linkage rules for a specific trigger field
  applyLinkageRules: (triggerField: string) => void

  // --- Instruction-driven error handling interfaces ---
  // Set error for a single field
  setFieldError: (name: string, message: string | string[]) => void

  // Set errors for multiple fields in bulk
  setErrors: (errors: Record<string, string | string[]>) => void

  // Clear errors for specific fields or all fields if no names are provided
  clearErrors: (fieldNames?: string | string[]) => void
}
